import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { ClipboardCheck, AlertTriangle } from "lucide-react";

/**
 * Vehicle Damage Checklist Section for Rental Agreement
 */
export const DamageChecklistSection = ({ damageChecklist, setDamageChecklist, isEditing }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDamageCheckChange = (index, hasDamage) => {
    const updatedItems = [...damageChecklist.items];
    updatedItems[index] = { ...updatedItems[index], hasDamage };
    setDamageChecklist({ ...damageChecklist, items: updatedItems });
  };

  const handleDamageDescriptionChange = (index, description) => {
    const updatedItems = [...damageChecklist.items];
    updatedItems[index] = { ...updatedItems[index], description };
    setDamageChecklist({ ...damageChecklist, items: updatedItems });
  };

  return (
    <Card data-testid="damage-checklist-card">
      <CardHeader className="pb-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-primary" />
          VEHICLE DAMAGE CHECKLIST
        </CardTitle>
        <CardDescription>Document any existing damage before rental</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Inspection Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-3 bg-muted/30 rounded-lg">
          <div>
            <Label className="text-xs text-muted-foreground">Inspected By</Label>
            {isEditing ? (
              <Input 
                value={damageChecklist.inspectedBy || ''} 
                onChange={(e) => setDamageChecklist({...damageChecklist, inspectedBy: e.target.value})} 
                className="h-8 text-sm mt-1" 
                placeholder="Inspector name"
                data-testid="inspector-name-input"
              />
            ) : (
              <p className="text-sm font-medium">{damageChecklist.inspectedBy || '-'}</p>
            )}
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Inspection Date</Label>
            {isEditing ? (
              <Input 
                type="date" 
                value={damageChecklist.inspectionDate || ''} 
                onChange={(e) => setDamageChecklist({...damageChecklist, inspectionDate: e.target.value})} 
                className="h-8 text-sm mt-1"
                data-testid="inspection-date-input"
              />
            ) : (
              <p className="text-sm font-medium">
                {damageChecklist.inspectionDate ? formatDate(damageChecklist.inspectionDate) : '-'}
              </p>
            )}
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-muted-foreground">Damage Summary</Label>
            <p className="text-sm font-medium flex items-center gap-2">
              {damageChecklist.items?.filter(i => i.hasDamage).length || 0} area(s) with noted damage
              {damageChecklist.items?.filter(i => i.hasDamage).length > 0 && (
                <AlertTriangle className="w-4 h-4 text-warning" />
              )}
            </p>
          </div>
        </div>

        {/* Damage Checklist Grid */}
        {damageChecklist.items && damageChecklist.items.length > 0 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {damageChecklist.items.map((item, index) => (
                <div 
                  key={index} 
                  className={`p-3 rounded-lg border transition-colors ${
                    item.hasDamage 
                      ? 'bg-warning/5 border-warning/30' 
                      : 'bg-muted/30 border-transparent'
                  }`}
                  data-testid={`damage-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    {isEditing ? (
                      <Checkbox
                        id={`damage-${index}`}
                        checked={item.hasDamage}
                        onCheckedChange={(checked) => handleDamageCheckChange(index, checked)}
                        className="mt-0.5"
                        data-testid={`damage-checkbox-${index}`}
                      />
                    ) : (
                      <div className={`w-5 h-5 rounded flex items-center justify-center ${
                        item.hasDamage ? 'bg-warning/20' : 'bg-success/20'
                      }`}>
                        {item.hasDamage ? (
                          <AlertTriangle className="w-3 h-3 text-warning" />
                        ) : (
                          <ClipboardCheck className="w-3 h-3 text-success" />
                        )}
                      </div>
                    )}
                    <div className="flex-1">
                      <label 
                        htmlFor={`damage-${index}`}
                        className={`text-sm font-medium cursor-pointer ${
                          item.hasDamage ? 'text-warning' : 'text-foreground'
                        }`}
                      >
                        {item.area}
                      </label>
                      {item.hasDamage && isEditing && (
                        <Input
                          value={item.description || ''}
                          onChange={(e) => handleDamageDescriptionChange(index, e.target.value)}
                          placeholder="Describe the damage..."
                          className="h-7 text-xs mt-2"
                          data-testid={`damage-description-${index}`}
                        />
                      )}
                      {item.hasDamage && !isEditing && item.description && (
                        <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Additional Notes */}
            <Separator />
            <div>
              <Label className="text-xs text-muted-foreground uppercase mb-2 block">
                Additional Damage Notes / Comments
              </Label>
              {isEditing ? (
                <Textarea
                  value={damageChecklist.additionalNotes || ''}
                  onChange={(e) => setDamageChecklist({...damageChecklist, additionalNotes: e.target.value})}
                  placeholder="Enter any additional notes about vehicle condition, pre-existing damage, or special considerations..."
                  rows={3}
                  data-testid="damage-additional-notes"
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  {damageChecklist.additionalNotes || 'No additional notes.'}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No damage checklist available.</p>
            {isEditing && (
              <p className="text-xs mt-2">Create a new agreement to generate a damage checklist.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DamageChecklistSection;
